import { useCallback, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import strengthCalculator from '@app/utils/passwordStrengthCalculator';
import sportsApi from '@app/utils/sportsApi';

export type FormValues = {
    password: string;
    newPassword: string;
    repeatPassword: string;
};

export const initialValues: FormValues = {
    password: '',
    newPassword: '',
    repeatPassword: '',
};

export const usePasswordform = (user, sendNotification) => {
    const { t } = useTranslation('settings');

    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.password) {
            errors.password = t(
                'userSettings.password.messages.currentPasswordRequired',
            );
        }
        if (!values.newPassword) {
            errors.newPassword = t('userSettings.password.messages.required');
        }
        if (!values.repeatPassword) {
            errors.repeatPassword = t(
                'userSettings.password.messages.required',
            );
        }
        if (
            values.repeatPassword &&
            values.newPassword &&
            values.repeatPassword !== values.newPassword
        ) {
            errors.repeatPassword = t(
                'userSettings.password.messages.passwordsMustMatch',
            );
        }

        return errors;
    };

    const formik = useFormik({
        initialValues,
        onSubmit: async (values, props) => {
            const { id } = user;

            const payload = {
                domainObject: {
                    password: values.newPassword,
                },
                password: values.password,
            };

            await sportsApi
                .url(`/users/${id}`)
                .json(payload)
                .put()
                .badRequest(error => {
                    const errorResponse = JSON.parse(error.message);
                    if (typeof errorResponse !== 'object') return;

                    props.setErrors({
                        password: errorResponse.message,
                        newPassword: errorResponse.message,
                    });
                })
                .forbidden(error => {
                    const errorResponse = JSON.parse(error.message);
                    if (typeof errorResponse !== 'object') return;

                    props.setErrors({
                        password: errorResponse.message,
                        newPassword: errorResponse.message,
                    });

                    if (!errorResponse.ssoError) return;

                    sendNotification(
                        t('userSettings.password.messages.updateError'),
                        {
                            context: 'danger',
                            description: t(
                                'userSettings.password.messages.ssoDescription',
                            ),
                        },
                    );
                })
                .res(() => {
                    sendNotification(
                        t('userSettings.password.messages.updateSuccess'),
                        { context: 'success' },
                    );

                    props.resetForm();
                });
        },
        validate,
    });

    const [passwordStrength, setPasswordStrength] = useState(
        strengthCalculator(''),
    );

    const totalCriteria = 5;
    const metCriteria = [
        passwordStrength.lengthRequirement,
        passwordStrength.uppercaseExists,
        passwordStrength.lowercaseExists,
        passwordStrength.numberExists,
        passwordStrength.specialCharExists,
    ].filter(Boolean).length;

    const percent = useMemo(() => {
        if (!formik.values.newPassword) return 0;

        return Math.round((metCriteria / totalCriteria) * 100);
    }, [metCriteria, totalCriteria, formik.values.newPassword]);

    const checkPasswordStrength = useCallback(
        (handleChange: any) => (e: any) => {
            const strengthObj = strengthCalculator(e.target.value);
            setPasswordStrength(strengthObj);
            handleChange(e);
        },
        [],
    );

    const clearAllFields = () => {
        formik.resetForm();
    };

    const savePassword = () => {
        formik.submitForm();
    };

    const isSaveDisabled = useMemo(() => {
        const allFieldsValid =
            formik.values.password &&
            formik.values.newPassword &&
            formik.values.repeatPassword;
        const passwordsMatch =
            formik.values.newPassword === formik.values.repeatPassword;
        return !allFieldsValid || !passwordsMatch || formik.isSubmitting;
    }, [formik.values]);

    const passwordStatus: 'exception' | 'active' = useMemo(() => {
        if (formik.values.newPassword !== formik.values.repeatPassword)
            return 'exception';

        return 'active';
    }, [formik.values.newPassword, formik.values.repeatPassword]);

    return {
        formik,
        checkPasswordStrength,
        percent,
        clearAllFields,
        savePassword,
        isSaveDisabled,
        passwordStatus,
    };
};
